import { useState } from 'react';
import { SIGNAL_FAMILIES } from '../scoring/scoringConfig';
import { familyLabel } from '../familyMeta';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';

function downloadConfig(config) {
  const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'menagtm-scoring-config.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ConsoleRail({
  sourceOptions,
  enabledSources,
  onToggleSource,
  config,
  sliderRanges,
  updateWeight,
  updateField,
  resetToDefaults,
  justSaved,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  return (
    <aside className="rail">
      <div className="rail-section">
        <div className="rail-title">
          SOURCES
          <span className={`save-flag${justSaved ? ' show' : ''}`}>saved</span>
        </div>
        {sourceOptions.length === 0 && (
          <div className="hint">No signals ingested yet — nothing to filter by source.</div>
        )}
        {sourceOptions.map(({ source, label, count }) => (
          <div className="src-row" key={source}>
            <label>
              <input
                type="checkbox"
                checked={enabledSources.has(source)}
                onChange={() => onToggleSource(source)}
              />
              {label}
            </label>
            <span className="src-count">{count}</span>
          </div>
        ))}
      </div>

      <div className="rail-section advanced-toggle-section">
        <button
          type="button"
          className="advanced-toggle"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />} Advanced options
        </button>
      </div>

      {advancedOpen && (
        <div
          className="advanced-options-scroll"
          role="region"
          aria-label="Advanced scoring options"
          tabIndex={0}
        >
          <div className="rail-section">
            <div className="rail-title">SCORING WEIGHTS</div>
            {SIGNAL_FAMILIES.map((family) => (
              <div className="slider-block" key={family}>
                <div className="slider-head">
                  <span>{familyLabel(family)}</span>
                  <span className="val">{config.weights[family]}</span>
                </div>
                <input
                  type="range"
                  min={sliderRanges.weight.min}
                  max={sliderRanges.weight.max}
                  step={sliderRanges.weight.step}
                  value={config.weights[family]}
                  onChange={(e) => updateWeight(family, Number(e.target.value))}
                />
              </div>
            ))}
          </div>

          <div className="rail-section">
            <div className="rail-title">TRIANGULATION</div>
            <div className="slider-block">
              <div className="slider-head">
                <span>Signal half-life</span>
                <span className="val">{config.halfLifeDays} days</span>
              </div>
              <input
                type="range"
                min={sliderRanges.halfLifeDays.min}
                max={sliderRanges.halfLifeDays.max}
                value={config.halfLifeDays}
                onChange={(e) => updateField('halfLifeDays', Number(e.target.value))}
              />
            </div>
            <div className="slider-block">
              <div className="slider-head">
                <span>Corroboration minimum</span>
                <span className="val">{config.corroborationMinFamilies} families</span>
              </div>
              <input
                type="range"
                min={sliderRanges.corroborationMinFamilies.min}
                max={sliderRanges.corroborationMinFamilies.max}
                value={config.corroborationMinFamilies}
                onChange={(e) => updateField('corroborationMinFamilies', Number(e.target.value))}
              />
            </div>
            <div className="slider-block">
              <div className="slider-head">
                <span>Recent evidence window</span>
                <span className="val">{config.corroborationWindowDays} days</span>
              </div>
              <input
                type="range"
                min={sliderRanges.corroborationWindowDays.min}
                max={sliderRanges.corroborationWindowDays.max}
                value={config.corroborationWindowDays}
                onChange={(e) => updateField('corroborationWindowDays', Number(e.target.value))}
              />
            </div>
            <div className="slider-block">
              <div className="slider-head">
                <span>Corroboration bonus</span>
                <span className="val">×{config.corroborationBonus.toFixed(2)}</span>
              </div>
              <input
                type="range"
                min={sliderRanges.corroborationBonus.min}
                max={sliderRanges.corroborationBonus.max}
                step={sliderRanges.corroborationBonus.step}
                value={config.corroborationBonus}
                onChange={(e) => updateField('corroborationBonus', Number(e.target.value))}
              />
            </div>
          </div>

          <div className="rail-section">
            <div className="rail-title">TIERING</div>
            <div className="slider-block">
              <div className="slider-head">
                <span>Tier A starts at</span>
                <span className="val">{config.tierAMin}</span>
              </div>
              <input
                type="range"
                min={Math.max(sliderRanges.tierAMin.min, config.tierBMin + 1)}
                max={sliderRanges.tierAMin.max}
                value={config.tierAMin}
                onChange={(e) => updateField('tierAMin', Number(e.target.value))}
              />
            </div>
            <div className="slider-block">
              <div className="slider-head">
                <span>Tier B starts at</span>
                <span className="val">{config.tierBMin}</span>
              </div>
              <input
                type="range"
                min={sliderRanges.tierBMin.min}
                max={Math.min(sliderRanges.tierBMin.max, config.tierAMin - 1)}
                value={config.tierBMin}
                onChange={(e) => updateField('tierBMin', Number(e.target.value))}
              />
            </div>
            <div className="hint">
              Tier A also requires the corroboration minimum within the recent evidence window.
              A single signal family cannot become Tier A by itself.
            </div>
          </div>

          <div className="rail-actions">
            <button className="btn" onClick={resetToDefaults}>Reset defaults</button>
            <button className="btn primary" onClick={() => downloadConfig(config)}><Download size={15} aria-hidden="true" />Export config</button>
          </div>
        </div>
      )}
    </aside>
  );
}
